import React, { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GUESTHOUSE_API_BASE = `${API_ROOT}/api/guesthouse`;

const nw = { whiteSpace: "nowrap" };

const PAYMENT_MODE_LABELS = {
  P: "Pending",
  C: "Cash",
  Q: "Cheque",
  O: "Online",
  F: "Free Charge",
};

function rowBgColor(paymentMode) {
  if (paymentMode === "P" || paymentMode === "") return "lightcoral";
  if (paymentMode === "C" || paymentMode === "Q" || paymentMode === "O") return "lightgreen";
  return "";
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function displayVisitType(vt) {
  if (vt === "O") return "Official";
  if (vt === "P") return "Personal";
  return vt || "";
}

export default function GuesthousePaymentDetails() {
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);

  const [guesthouses, setGuesthouses] = useState([]);
  const [guestHouseID, setGuestHouseID] = useState("0");
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [paymentMode, setPaymentMode] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (![10, 25].includes(moduleId)) return;
    fetch(`${GUESTHOUSE_API_BASE}/booking/guesthouses?moduleId=${moduleId}`)
      .then((r) => r.json())
      .then((data) => setGuesthouses(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [moduleId]);

  if (![10, 25].includes(moduleId)) {
    return (
      <section className="container-fluid">
        <div className="alert alert-danger mt-3">Unauthorized access Or Session Timed out.</div>
      </section>
    );
  }

  const fetchPaymentDetails = async () => {
    if (!fromDate || !toDate) {
      setError("Please select both From Date and To Date.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setRows([]);

      const q = new URLSearchParams({ moduleId: String(moduleId), guestHouseID, fromDate, toDate, paymentMode });
      const res = await fetch(`${GUESTHOUSE_API_BASE}/payment-details?${q}`);
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to fetch payment details");

      setRows(Array.isArray(data) ? data : []);
      setSearched(true);
    } catch (err) {
      setError(err.message || "Failed to fetch payment details");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (bookingId) => {
    window.open(
      `/guesthouse/booking-edit/${encodeURIComponent(bookingId)}`,
      "_blank",
      "width=900,height=500,noopener,noreferrer"
    );
  };

  const openForm = (bookingId) => {
    const q = new URLSearchParams({ moduleId: String(moduleId), userId: String(user?.id || "") });
    window.open(
      `${GUESTHOUSE_API_BASE}/booking/print/${encodeURIComponent(bookingId)}?${q}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section className="container-fluid">
      <h3 className="ctitle">Payment Details</h3>

      <div style={{ textAlign: "left", marginTop: "10px", marginBottom: "16px" }}>
        <a href="/individual-ledger" className="btn btn-primary"
          style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}>
          Booking Details
        </a>
      </div>

      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label">Guest House</label>
          <select className="form-select" value={guestHouseID} onChange={(e) => setGuestHouseID(e.target.value)}>
            <option value="0">All Guest House</option>
            {guesthouses.map((gh) => (
              <option key={gh.GuestHouseID} value={String(gh.GuestHouseID)}>
                {gh.GuestHouse}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">From Date</label>
          <input className="form-control" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>

        <div className="col-md-2">
          <label className="form-label">To Date</label>
          <input className="form-control" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <div className="col-md-2">
          <label className="form-label">Payment Mode</label>
          <select className="form-select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
            <option value="ALL">All</option>
            <option value="P">Pending</option>
            <option value="C">Cash</option>
            <option value="Q">Cheque</option>
            <option value="O">Online</option>
            <option value="F">Free Charge</option>
          </select>
        </div>

        <div className="col-md-1">
          <label className="form-label">&nbsp;</label>
          <button type="button" className="btn btn-primary w-100" onClick={fetchPaymentDetails} disabled={loading}>
            View
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="text-muted mb-2">Loading...</div>}

      {!loading && searched && (
        <div className="table-responsive">
          <table className="table table-bordered align-middle" style={{ borderCollapse: "collapse" }}>
            <thead style={{ "--bs-table-bg": "#008080", color: "white" }}>
              <tr>
                <th style={nw}>JobID</th>
                <th style={nw}>BID</th>
                <th style={nw}>Visitor Name</th>
                <th style={nw}>Booked By</th>
                <th style={nw}>Type</th>
                <th style={nw}>Room</th>
                <th style={nw}>Check-In</th>
                <th style={nw}>Check-Out</th>
                <th style={nw}>Amt</th>
                <th style={nw}>GST</th>
                <th style={nw}>TAmt</th>
                <th style={nw}>Invoice No</th>
                <th style={nw}>Inv Dt</th>
                <th style={nw}>Mode</th>
                <th style={nw}>Transaction ID</th>
                <th style={nw}>GHouse</th>
                <th style={nw}>Edit</th>
                <th style={nw}>Form</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={18} className="text-center text-muted">No records found.</td>
                </tr>
              )}
              {rows.map((row) => {
                const amount    = parseFloat(row.Amount) || 0;
                const gstPct    = parseFloat(row.GST)    || 0;
                const gstAmount = Math.round((amount * gstPct) / 100);
                const totalAmt  = Math.round(amount + gstAmount);
                const pm        = row.PaymentMode || "";

                return (
                  <tr key={`${row.JobID}-${row.BookingID}`} style={{ backgroundColor: rowBgColor(pm) }}>
                    <td style={nw}>{row.JobID}</td>
                    <td style={nw}>{row.BookingID}</td>
                    <td style={{ ...nw, textAlign: "left" }}>{row.VisitorName || ""}</td>
                    <td style={nw}>{row.PersonName || ""}</td>
                    <td style={nw}>{displayVisitType(row.TypeOfVisit)}</td>
                    <td style={nw}>{row.Room || ""}</td>
                    <td style={nw}>{formatDate(row.CheckIn)}</td>
                    <td style={nw}>{row.CheckOut ? formatDate(row.CheckOut) : "NULL"}</td>
                    <td style={nw}>{amount || ""}</td>
                    <td style={nw}>{gstAmount || ""}</td>
                    <td style={nw}>{totalAmt || ""}</td>
                    <td style={nw}>{row.ReceiptNo || ""}</td>
                    <td style={nw}>{formatDate(row.ReceiptDate)}</td>
                    <td style={nw}>{PAYMENT_MODE_LABELS[pm] || pm || "Pending"}</td>
                    <td style={nw}>{row.UPIID || ""}</td>
                    <td style={nw}>{row.GuestHouseAbb || ""}</td>
                    <td style={nw}>
                      <button type="button" className="btn btn-sm btn-outline-warning"
                        onClick={() => openEdit(row.BookingID)}>
                        Edit
                      </button>
                    </td>
                    <td style={nw}>
                      <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => openForm(row.BookingID)}>
                        Form
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
